import { fetchJSON, HttpError } from '../utils/fetchClient';
import { ConfigLoader } from '../config';
import { createLogger } from '../utils/logger';

const log = createLogger('AuthService');

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export class AuthService {
  private readonly baseUrl: string;
  private token: string | null = null;
  private tokenExpiry: Date | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private tokenRequestPromise: Promise<string> | null = null;
  
  // Observations API token (separate from weather stations token)
  private observationsToken: string | null = null;
  private observationsTokenExpiry: Date | null = null;
  private observationsRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private observationsTokenRequestPromise: Promise<string> | null = null;
  
  // Station Manager API token
  private stationManagerToken: string | null = null;
  private stationManagerTokenExpiry: Date | null = null;
  private stationManagerRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private stationManagerTokenRequestPromise: Promise<string> | null = null;
  
  private readonly REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry
  private readonly MAX_RETRIES = 3;
  private readonly INITIAL_RETRY_DELAY_MS = 1000;

  constructor() {
    // Always use window.location.origin so requests route through the Vite proxy.
    // The proxy forwards /api/* to DEV_BACKEND_PORT — hardcoding 3001 breaks multi-worktree setups.
    this.baseUrl = window.location.origin;
    log.info(`Base URL: ${this.baseUrl}`);
  }

  /**
   * Initialize the authentication service by requesting the first token
   */
  async initialize(): Promise<void> {
    await this.getAccessToken();
  }

  /**
   * Get a valid access token, requesting a new one if necessary
   */
  async getAccessToken(): Promise<string> {
    // If a token request is already in progress, wait for it
    if (this.tokenRequestPromise) {
      return this.tokenRequestPromise;
    }

    // If we have a valid token, return it
    if (this.token && !this.isTokenExpired()) {
      return this.token;
    }

    // Request a new token
    this.tokenRequestPromise = this.requestTokenWithRetry()
      .finally(() => {
        this.tokenRequestPromise = null;
      });

    return this.tokenRequestPromise;
  }

  /**
   * Request a new token with retry logic
   */
  private async requestTokenWithRetry(retryCount = 0): Promise<string> {
    try {
      const tokenResponse = await this.requestToken();
      this.token = tokenResponse.access_token;
      
      // Calculate expiry time
      const expiryDate = new Date();
      expiryDate.setSeconds(expiryDate.getSeconds() + tokenResponse.expires_in);
      this.tokenExpiry = expiryDate;

      // Schedule automatic refresh
      this.scheduleTokenRefresh(tokenResponse.expires_in);

      return this.token;
    } catch (error) {
      if (retryCount < this.MAX_RETRIES) {
        // Exponential backoff
        const delay = this.INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.requestTokenWithRetry(retryCount + 1);
      }
      
      throw new Error(
        `Authentication failed after ${this.MAX_RETRIES} retries: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Request a new token from the OAuth 2.0 endpoint via proxy
   */
  private async requestToken(): Promise<TokenResponse> {
    const config = ConfigLoader.get();
    
    if (!config) {
      throw new Error('Configuration not loaded');
    }

    // Use proxy server to avoid CORS issues
    const proxyUrl = `${this.baseUrl}/api/auth/token`;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      try {
        const data = await fetchJSON<TokenResponse>(proxyUrl, { method: 'POST', signal: controller.signal });
        if (!data.access_token || !data.expires_in) {
          throw new Error('Invalid token response: missing required fields');
        }
        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      if (error instanceof HttpError) {
        throw new Error(`Token request failed with status ${error.status}: ${error.body}`);
      }
      if (error instanceof TypeError) {
        throw new Error(`Token request failed: No response from ${proxyUrl}. Verify the API endpoint is accessible.`);
      }
      throw error;
    }
  }

  /**
   * Check if the current token is expired or about to expire
   */
  private isTokenExpired(): boolean {
    if (!this.tokenExpiry) {
      return true;
    }

    const now = new Date();
    const timeUntilExpiry = this.tokenExpiry.getTime() - now.getTime();
    
    // Consider token expired if it expires within the refresh buffer
    return timeUntilExpiry <= this.REFRESH_BUFFER_MS;
  }

  /**
   * Schedule automatic token refresh before expiry
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    // Clear any existing refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Calculate when to refresh (5 minutes before expiry)
    const refreshIn = (expiresIn * 1000) - this.REFRESH_BUFFER_MS;
    
    // Ensure we don't schedule a negative timeout
    if (refreshIn > 0) {
      this.refreshTimer = setTimeout(async () => {
        try {
          await this.getAccessToken();
        } catch (error) {
          log.error('Automatic token refresh failed:', error);
        }
      }, refreshIn);
    }
  }

  /**
   * Get a valid observations API access token
   */
  async getObservationsAccessToken(): Promise<string> {
    // If a token request is already in progress, wait for it
    if (this.observationsTokenRequestPromise) {
      return this.observationsTokenRequestPromise;
    }

    // If we have a valid token, return it
    if (this.observationsToken && !this.isObservationsTokenExpired()) {
      return this.observationsToken;
    }

    // Request a new token
    this.observationsTokenRequestPromise = this.requestObservationsTokenWithRetry()
      .finally(() => {
        this.observationsTokenRequestPromise = null;
      });

    return this.observationsTokenRequestPromise;
  }

  /**
   * Request observations token with retry logic
   */
  private async requestObservationsTokenWithRetry(retryCount = 0): Promise<string> {
    try {
      const tokenResponse = await this.requestObservationsToken();
      this.observationsToken = tokenResponse.access_token;
      
      // Calculate expiry time
      const expiryDate = new Date();
      expiryDate.setSeconds(expiryDate.getSeconds() + tokenResponse.expires_in);
      this.observationsTokenExpiry = expiryDate;

      // Schedule automatic refresh
      this.scheduleObservationsTokenRefresh(tokenResponse.expires_in);

      return this.observationsToken;
    } catch (error) {
      if (retryCount < this.MAX_RETRIES) {
        // Exponential backoff
        const delay = this.INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.requestObservationsTokenWithRetry(retryCount + 1);
      }
      
      throw new Error(
        `Observations authentication failed after ${this.MAX_RETRIES} retries: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Request observations token from OAuth endpoint via proxy
   */
  private async requestObservationsToken(): Promise<TokenResponse> {
    // Use the consolidated auth endpoint (server/routes/auth.js) — same path
    // ObservationsAPI.ts uses successfully. The old `/api/observations/auth/token`
    // had NO handler: it fell through to the observations proxy catch-all, which
    // 401'd (no bearer — the whole point was to GET one), then the frontend retried
    // 3× with backoff = 7–127s of dead time. This is the #1 "search takes >1 min" cause.
    const proxyUrl = `${this.baseUrl}/api/auth/token?service=observations`;
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      try {
        const data = await fetchJSON<TokenResponse>(proxyUrl, { method: 'POST', signal: controller.signal });
        if (!data.access_token || !data.expires_in) {
          throw new Error('Invalid observations token response: missing required fields');
        }
        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      if (error instanceof HttpError) {
        throw new Error(`Observations token request failed with status ${error.status}: ${error.body}`);
      }
      if (error instanceof TypeError) {
        throw new Error(`Observations token request failed: No response from ${proxyUrl}. Verify the API endpoint is accessible.`);
      }
      throw error;
    }
  }

  /**
   * Check if observations token is expired
   */
  private isObservationsTokenExpired(): boolean {
    if (!this.observationsTokenExpiry) {
      return true;
    }

    const now = new Date();
    const timeUntilExpiry = this.observationsTokenExpiry.getTime() - now.getTime();
    
    return timeUntilExpiry <= this.REFRESH_BUFFER_MS;
  }

  /**
   * Schedule automatic observations token refresh
   */
  private scheduleObservationsTokenRefresh(expiresIn: number): void {
    if (this.observationsRefreshTimer) {
      clearTimeout(this.observationsRefreshTimer);
    }

    const refreshIn = (expiresIn * 1000) - this.REFRESH_BUFFER_MS;
    
    if (refreshIn > 0) {
      this.observationsRefreshTimer = setTimeout(async () => {
        try {
          await this.getObservationsAccessToken();
        } catch (error) {
          log.error('Automatic observations token refresh failed:', error);
        }
      }, refreshIn);
    }
  }

  /**
   * Get a valid Station Manager API access token
   */
  async getStationManagerAccessToken(): Promise<string> {
    if (this.stationManagerTokenRequestPromise) {
      return this.stationManagerTokenRequestPromise;
    }

    if (this.stationManagerToken && !this.isStationManagerTokenExpired()) {
      return this.stationManagerToken;
    }

    this.stationManagerTokenRequestPromise = this.requestStationManagerTokenWithRetry()
      .finally(() => {
        this.stationManagerTokenRequestPromise = null;
      });

    return this.stationManagerTokenRequestPromise;
  }

  /**
   * Request Station Manager token with retry logic
   */
  private async requestStationManagerTokenWithRetry(retryCount = 0): Promise<string> {
    try {
      const tokenResponse = await this.requestStationManagerToken();
      this.stationManagerToken = tokenResponse.access_token;
      
      const expiryDate = new Date();
      expiryDate.setSeconds(expiryDate.getSeconds() + tokenResponse.expires_in);
      this.stationManagerTokenExpiry = expiryDate;

      this.scheduleStationManagerTokenRefresh(tokenResponse.expires_in);

      return this.stationManagerToken;
    } catch (error) {
      if (retryCount < this.MAX_RETRIES) {
        const delay = this.INITIAL_RETRY_DELAY_MS * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.requestStationManagerTokenWithRetry(retryCount + 1);
      }
      
      throw new Error(
        `Station Manager authentication failed after ${this.MAX_RETRIES} retries: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Request Station Manager token from OAuth endpoint via proxy
   */
  private async requestStationManagerToken(): Promise<TokenResponse> {
    // Hit the consolidated endpoint directly. The old `/api/station-manager/auth/token`
    // only "worked" via a 307 redirect whose POST-method preservation is unreliable
    // across fetch runtimes; go straight to the real handler like the observations path.
    const proxyUrl = `${this.baseUrl}/api/auth/token?service=stationManager`;
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      try {
        const data = await fetchJSON<TokenResponse>(proxyUrl, { method: 'POST', signal: controller.signal });
        if (!data.access_token || !data.expires_in) {
          throw new Error('Invalid Station Manager token response: missing required fields');
        }
        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      if (error instanceof HttpError) {
        throw new Error(`Station Manager token request failed with status ${error.status}: ${error.body}`);
      }
      if (error instanceof TypeError) {
        throw new Error(`Station Manager token request failed: No response from ${proxyUrl}. Verify the API endpoint is accessible.`);
      }
      throw error;
    }
  }

  /**
   * Check if Station Manager token is expired
   */
  private isStationManagerTokenExpired(): boolean {
    if (!this.stationManagerTokenExpiry) {
      return true;
    }

    const now = new Date();
    const timeUntilExpiry = this.stationManagerTokenExpiry.getTime() - now.getTime();
    
    return timeUntilExpiry <= this.REFRESH_BUFFER_MS;
  }

  /**
   * Schedule automatic Station Manager token refresh
   */
  private scheduleStationManagerTokenRefresh(expiresIn: number): void {
    if (this.stationManagerRefreshTimer) {
      clearTimeout(this.stationManagerRefreshTimer);
    }

    const refreshIn = (expiresIn * 1000) - this.REFRESH_BUFFER_MS;
    
    if (refreshIn > 0) {
      this.stationManagerRefreshTimer = setTimeout(async () => {
        try {
          await this.getStationManagerAccessToken();
        } catch (error) {
          log.error('Automatic Station Manager token refresh failed:', error);
        }
      }, refreshIn);
    }
  }

  /**
   * Clear the token and cancel any scheduled refresh
   */
  clearToken(): void {
    this.token = null;
    this.tokenExpiry = null;
    
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Also clear observations token
    this.observationsToken = null;
    this.observationsTokenExpiry = null;
    
    if (this.observationsRefreshTimer) {
      clearTimeout(this.observationsRefreshTimer);
      this.observationsRefreshTimer = null;
    }

    // Also clear Station Manager token
    this.stationManagerToken = null;
    this.stationManagerTokenExpiry = null;
    
    if (this.stationManagerRefreshTimer) {
      clearTimeout(this.stationManagerRefreshTimer);
      this.stationManagerRefreshTimer = null;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
